import { PARENT_MAIL_LABEL } from "./scheduleLabels";
import {
  isSchoolEventDismissed,
  isSchoolEventDuplicate,
  resolveSchoolEvent,
} from "./resolveSchoolEvent";
import { getShareableKiTaEvents, canShareKiTaExtract } from "./shareKiTaWin";
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl } from "./googleCalendarLink";

/**
 * Dated attention items from one extraction (matches push composer semantics).
 * @param {{ events?: object[], corrections?: object|null }} row
 */
export function listExtractionDeadlineItems({ events = [], corrections = null } = {}) {
  const items = [];
  (events || []).forEach((rawEvent, index) => {
    if (isSchoolEventDismissed(corrections, index)) return;
    if (isSchoolEventDuplicate(rawEvent)) return;
    const event = resolveSchoolEvent(rawEvent, corrections, index);
    if (!event || isSchoolEventDuplicate(event)) return;
    const actionType = String(event?.action_type || "").toLowerCase();
    if (actionType === "info") return;
    if (!event?.date) return;
    items.push({ ...event, eventIndex: index });
  });
  items.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return String(a.time || "").localeCompare(String(b.time || ""));
  });
  return items;
}

/**
 * @param {{
 *   subject?: string|null,
 *   events?: object[],
 *   corrections?: object|null,
 *   pdfProcessed?: boolean,
 *   summary?: string|null,
 *   extractionId?: string|null,
 * }} params
 */
export function buildKitaExtractionArrivalMessage({
  subject,
  events = [],
  corrections = null,
  pdfProcessed = false,
  summary = null,
  extractionId = null,
} = {}) {
  const items = listExtractionDeadlineItems({ events, corrections });
  const subjectLabel = String(subject || "").trim();
  const summaryText = String(summary || "").trim();
  const shareEvents = getShareableKiTaEvents({ events, corrections });

  if (items.length === 0) {
    const detail = subjectLabel ? ` „${subjectLabel}"` : "";
    return {
      text: `${PARENT_MAIL_LABEL}${detail} ist da — ich habe nichts Dringendes erkannt.`,
      showAgenda: false,
      extractionArrival: {
        extractionId,
        summary: summaryText,
        shareEvents: [],
        calendarLinks: [],
        canShare: canShareKiTaExtract({ summary: summaryText, events: shareEvents }),
      },
    };
  }

  const lineItems = items.slice(0, 5);
  const lines = lineItems.map((item) => ({
    eventIndex: item.eventIndex,
    title: item.title || "Termin",
    date: item.date,
    time: item.time || "",
    all_day: item.all_day !== false && !item.time,
    ...(item.end_date && item.end_date !== item.date ? { end_date: item.end_date } : {}),
  }));
  const moreCount = items.length - lines.length;

  const chatIntro = pdfProcessed
    ? `${PARENT_MAIL_LABEL} und PDF ausgewertet`
    : `Neue ${PARENT_MAIL_LABEL}`;
  const text = `${chatIntro}${subjectLabel ? `: „${subjectLabel}"` : ""}. Ich erinnere dich vor jeder Frist.`;

  const calendarLinks = lineItems
    .map((event) => ({
      eventIndex: event.eventIndex,
      title: event.title || "Termin",
      calUrl: buildGoogleCalendarUrl(event),
      outlookUrl: buildOutlookCalendarUrl(event),
    }))
    .filter((link) => link.calUrl || link.outlookUrl);

  return {
    text,
    showAgenda: true,
    expandAgenda: true,
    agendaExtractionId: extractionId,
    content: "kita-extraction-arrival",
    extractionArrival: {
      extractionId,
      summary: summaryText,
      shareEvents,
      lines,
      moreCount,
      calendarLinks,
      canShare: canShareKiTaExtract({ summary: summaryText, events: shareEvents }),
    },
  };
}

/**
 * Apply a saved title or date correction onto the arrival card.
 * @param {object} arrival
 * @param {number} eventIndex
 * @param {object} resolved
 */
export function applyArrivalLinePatch(arrival, eventIndex, resolved) {
  const nextEvent = {
    title: resolved?.title,
    date: resolved?.date,
    time: resolved?.all_day === true ? null : resolved?.time || null,
    all_day: resolved?.all_day === true || !resolved?.time,
  };
  const lines = (arrival?.lines || []).map((line) => {
    if (line.eventIndex !== eventIndex) return line;
    return {
      ...line,
      title: nextEvent.title || line.title,
      date: nextEvent.date || line.date,
      time: nextEvent.all_day ? "" : nextEvent.time || "",
      all_day: nextEvent.all_day,
    };
  });
  const shareEvents = (arrival?.shareEvents || []).map((event) => {
    if (event.index !== eventIndex) return event;
    return {
      ...event,
      title: nextEvent.title || event.title,
      date: nextEvent.date || event.date,
      time: nextEvent.all_day ? null : nextEvent.time,
      all_day: nextEvent.all_day,
    };
  });
  const calendarLinks = (arrival?.calendarLinks || []).map((link) => {
    if (link.eventIndex !== eventIndex) return link;
    const event = shareEvents.find((row) => row.index === eventIndex) || {
      title: nextEvent.title || link.title,
      date: nextEvent.date,
      time: nextEvent.time,
      all_day: nextEvent.all_day,
    };
    return {
      ...link,
      title: event.title || link.title,
      calUrl: buildGoogleCalendarUrl(event),
      outlookUrl: buildOutlookCalendarUrl(event),
    };
  });
  return { ...arrival, lines, shareEvents, calendarLinks };
}

/**
 * @param {number} totalToday
 */
export function buildKitaExtractionBatchArrivalMessage(totalToday) {
  const count = Math.max(1, totalToday);
  return {
    text: `Heute sind ${count} ${PARENT_MAIL_LABEL}s angekommen. Ich erinnere dich an Termine und Fristen.`,
    showAgenda: false,
  };
}

/** @param {string} iso */
export function receivedOnZurichDate(iso, todayYmd) {
  if (!iso) return false;
  const local = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  return local === todayYmd;
}
